import {
    CartItem,
    ProductInstance,
    TicketOption,
    TicketOptionWithQuantity,
    TicketType,
    AppliedGiftCard,
    BilberryGiftcardStatus,
    BilberryPromoCodeStatus,
    AppliedPromoCode,
    MembershipIntentMultiResponse,
    MembershipUserValueCard,
    MembershipCurrencyCost,
    NativeCalculateResult,
    OctoBooking,
} from '@repo/types';
import { sumBy } from 'lodash-es';
import { getLocaleNumberFormatNoDecimals, localeAtom } from '@repo/i18n';
import {
    isPromoCodeApplicableForProductPurchase,
    promocodeAsDiscountSource,
} from './discounts/promocodes';
import { isWaitingListTicketOption } from './cart/cartUtils';

export type PriceSummaryPromoCodeDiscount = {
    amount: number;
    percentage: number;
    numDiscountedQuantities: number;
    code: string;
};

export type PriceSummary = {
    creditsUsed?: number;
    totalPrice: number;
    priceToPayLater: number;
    totalPriceExDiscounts: number;
    totalPriceExVat: number;
    totalVat: number;
    vatAmountInfo: { [vat: string]: number };
    promoCodeDiscounts: PriceSummaryPromoCodeDiscount[];
};

export function createEmptyPriceSummary(): PriceSummary {
    return {
        totalPrice: 0,
        priceToPayLater: 0,
        totalPriceExDiscounts: 0,
        totalPriceExVat: 0,
        totalVat: 0,
        vatAmountInfo: {},
        creditsUsed: 0,
        promoCodeDiscounts: [],
    };
}

// Native membership: in native mode the widget displays Base's authoritative figures from
// /api/reserve/calculate rather than computing VAT cart-side. Overlay Base's coverage-aware
// vat_breakdown (net/tax per rate, already reflecting the member-card + promo discount) onto the
// price summary so the VAT rows match the charge. The item rows keep the cart's gross labels; only
// the VAT totals change. No-op when Base sent no breakdown (falls back to the cart-derived VAT).
export function getPriceSummaryWithNativeVat(
    base: PriceSummary,
    result: NativeCalculateResult,
): PriceSummary {
    const vatBreakdown = result.vat_breakdown ?? [];
    if (vatBreakdown.length === 0) return base;
    return {
        ...base,
        totalPriceExVat: sumBy(vatBreakdown, (v) => v.net),
        totalVat: sumBy(vatBreakdown, (v) => v.tax),
        vatAmountInfo: vatBreakdown.reduce(
            (acc, v) => ({ ...acc, [String(v.rate)]: (acc[String(v.rate)] ?? 0) + v.tax }),
            {} as PriceSummary['vatAmountInfo'],
        ),
    };
}

export function getPriceSummaryFromCartItems(
    cartItems: CartItem[],
    promoCodeStatus?: BilberryPromoCodeStatus | null,
): PriceSummary {
    const promoCodeUsageContext = {
        promoCodeUsagesPerOrder: new Map<string, number>(),
        promoCodeUsages: new Map<string, number>(),
    };

    return cartItems.reduce((acc, item) => {
        const ticketOptions = item.ticketOptions.filter((to) => to.quantity > 0);

        const priceSummaryForTicketOptions = getPriceSummaryFromTicketOptions(
            ticketOptions,
            item.ticketType,
            promoCodeStatus,
            promoCodeUsageContext,
        );

        acc.totalPrice += priceSummaryForTicketOptions.totalPrice;
        acc.priceToPayLater += priceSummaryForTicketOptions.priceToPayLater;
        acc.totalPriceExDiscounts += priceSummaryForTicketOptions.totalPriceExDiscounts;
        acc.totalPriceExVat += priceSummaryForTicketOptions.totalPriceExVat;
        acc.totalVat += priceSummaryForTicketOptions.totalVat;
        acc.vatAmountInfo = Object.entries(priceSummaryForTicketOptions.vatAmountInfo).reduce(
            (acc, [rate, vat]) => ({
                ...acc,
                [rate]: (acc[rate] ?? 0) + vat,
            }),
            acc.vatAmountInfo,
        );
        acc.creditsUsed! += priceSummaryForTicketOptions.creditsUsed ?? 0;
        acc.promoCodeDiscounts = acc.promoCodeDiscounts.concat(
            priceSummaryForTicketOptions.promoCodeDiscounts,
        );

        return acc;
    }, createEmptyPriceSummary());
}

export function getPriceSummaryFromOctoBookings(bookings: OctoBooking[]) {
    const allPricing = bookings.map((booking) => booking.pricing);
    const totalPrice = sumBy(allPricing, (pricing) => pricing.retail) / 100;
    const totalPriceExVat = sumBy(allPricing, (pricing) => pricing.net) / 100;

    const allIncludedTaxes = allPricing.flatMap((pricing) => pricing.includedTaxes);

    const vatAmountInfo: Record<string, number> = allIncludedTaxes.reduce(
        (acc, tax) => {
            const taxKey = tax.name.replace('%', '');
            if (!acc[taxKey]) {
                acc[taxKey] = 0;
            }
            acc[taxKey] += tax.retail / 100;
            return acc;
        },
        {} as Record<string, number>,
    );

    const priceSummary: PriceSummary = {
        totalPrice: totalPrice,
        priceToPayLater: 0,
        totalPriceExDiscounts: 0,
        totalPriceExVat: totalPriceExVat,
        totalVat: totalPrice - totalPriceExVat,
        vatAmountInfo: vatAmountInfo,
        creditsUsed: 0,
        promoCodeDiscounts: [],
    };

    return priceSummary;
}

export function getGiftcardSpent(giftcard: BilberryGiftcardStatus, totalPrice: number): number {
    let priceReduction;
    if (totalPrice < giftcard.balance) {
        priceReduction = totalPrice;
    } else {
        priceReduction = giftcard.balance;
    }

    return priceReduction;
}

export function getInitialQuantityData(
    prices: TicketOption[],
    minEntrants: number | undefined | null,
): TicketOptionWithQuantity[] {
    if (prices.length === 1) {
        return prices.map((price) => ({
            ...price,
            quantity: minEntrants ?? 1,
        }));
    }

    const initialAdults = minEntrants ? Math.ceil(minEntrants / 2) : 1;
    const initialChildren = minEntrants ? Math.floor(minEntrants / 2) : 0;

    return prices.map((price, i) => ({
        ...price,
        quantity: i === 0 ? initialAdults : i === 1 ? initialChildren : 0,
    }));
}

export function updateQuantityData(
    selectedProducts: readonly ProductInstance[] | undefined,
    defaultPrices: readonly TicketOption[],
    existingQuantities: readonly TicketOptionWithQuantity[],
): TicketOptionWithQuantity[] {
    const selectedProductPrices = selectedProducts?.[0]?.ticketOptions ?? [];
    const allPricesPreferSelected = defaultPrices.map(
        (price) =>
            selectedProductPrices?.find(
                (selectedPrice) => selectedPrice.ticketCategoryId === price.ticketCategoryId,
            ) ?? {
                ...price,
                // shouldDisablePrice: !!selectedProductPrices // TODO: Find a good way to disable prices
            },
    );

    return allPricesPreferSelected
        .map((price) => {
            const existingQuantity = existingQuantities.find(
                (quantity) => quantity.ticketCategoryId === price.ticketCategoryId,
            );

            return {
                ...price,
                quantity: existingQuantity?.quantity ?? 0,
            };
        })
        .filter((price) =>
            selectedProductPrices && selectedProductPrices.length > 0
                ? selectedProductPrices.some(
                      (spp) => spp.ticketCategoryId === price.ticketCategoryId,
                  )
                : true,
        );
}

/**
 * Get the price summary from ticket options.
 * If the {ticketType} arg is present, use price from that without multiplying by quantity.
 */
export function getPriceSummaryFromTicketOptions(
    ticketOptions: TicketOptionWithQuantity[],
    ticketType?: TicketType,
    promoCodeStatus?: BilberryPromoCodeStatus | null,
    promoCodeUsageContext?: {
        promoCodeUsagesPerOrder: Map<string, number>;
        promoCodeUsages: Map<string, number>;
    },
): PriceSummary {
    if (ticketType) {
        const priceToPaylater =
            ticketType.price && ticketType.depositAmount
                ? ticketType.price - ticketType.depositAmount
                : 0;
        return {
            totalPrice: ticketType.price,
            priceToPayLater: priceToPaylater,
            totalPriceExDiscounts: ticketType.price,
            totalPriceExVat: ticketType.price - ticketType.vatAmount,
            totalVat: ticketType.vatAmount,
            vatAmountInfo: ticketType.vatBreakdown.reduce(
                (acc, cur) => ({
                    ...acc,
                    [cur.rate]: (acc[cur.rate] ?? 0) + cur.vatAmount,
                }),
                {} as PriceSummary['vatAmountInfo'],
            ),
            promoCodeDiscounts: [],
        };
    }

    const { locale } = localeAtom.subject.value;
    return ticketOptions.reduce(
        (acc, ticketOption) => {
            const isWaitingListItem = isWaitingListTicketOption(ticketOption);

            if (isWaitingListItem) {
                return acc;
            }

            if (ticketOption.membership && ticketOption.membership.length > 0) {
                acc.totalPrice +=
                    sumBy(ticketOption.membership ?? [], (m) => m.currencyCost?.price ?? 0) / 100;

                acc.priceToPayLater += 0; // TODO: Membership does not support deposit prices yet

                acc.totalPriceExVat +=
                    sumBy(ticketOption.membership ?? [], (m) => m.currencyCost?.netPrice ?? 0) /
                    100;
                acc.totalVat +=
                    sumBy(ticketOption.membership ?? [], (m) => m.currencyCost?.vatAmount ?? 0) /
                    100;
                acc.vatAmountInfo =
                    (
                        ticketOption.membership?.flatMap((m) => m.currencyCost?.rates ?? []) ?? []
                    ).reduce((vbAcc, vbCur) => {
                        vbAcc[getLocaleNumberFormatNoDecimals(locale, vbCur.taxRate / 100)] =
                            (vbAcc[getLocaleNumberFormatNoDecimals(locale, vbCur.taxRate / 100)] ??
                                0) +
                            vbCur.taxAmount / 100;
                        return vbAcc;
                    }, acc.vatAmountInfo) ?? acc.vatAmountInfo;
                const creditCost =
                    sumBy(
                        ticketOption.membership?.flatMap((m) => m.valueCardUsages ?? []) ?? [],
                        'creditCost',
                    ) / 100;

                const promoData = ticketOption.membership.reduce(
                    (acc: NonNullable<PriceSummary['promoCodeDiscounts']>[0] | null, m) => {
                        if (m.promoData) {
                            const newValue = {
                                amount: (m.promoData?.amount ?? 0) / 100,
                                percentage:
                                    m.promoData?.promoType === 'percent'
                                        ? m.promoData.promoValue
                                        : 0,
                                code: m.promoData?.name,
                                numDiscountedQuantities: 1,
                            };
                            if (!acc) return newValue;

                            acc.amount += newValue.amount;
                            acc.percentage += newValue.percentage;
                            acc.numDiscountedQuantities += newValue.numDiscountedQuantities;
                            return acc;
                        }
                        return acc;
                    },
                    null,
                );
                acc.promoCodeDiscounts = acc.promoCodeDiscounts.concat(promoData ?? []);
                acc.totalPriceExDiscounts =
                    acc.totalPrice + acc.promoCodeDiscounts.reduce((acc, d) => acc + d.amount, 0);

                acc.creditsUsed! += creditCost;

                return acc;
            }

            const { price, discount, priceToPayLater } = getPriceFromTicketOptionNonMembership(
                ticketOption,
                ticketType,
                promoCodeStatus,
                promoCodeUsageContext,
            );
            const vatBreakdown = getVatBreakdownFromTicketOption(
                ticketOption,
                ticketType,
                discount?.amount,
            );

            const discountedPrice = price - (discount?.amount ?? 0);
            acc.totalPrice += discountedPrice;
            acc.totalPriceExDiscounts += price;
            acc.priceToPayLater += priceToPayLater;
            acc.totalPriceExVat += discountedPrice - vatBreakdown.vatAmount;
            acc.totalVat += vatBreakdown.vatAmount;
            acc.vatAmountInfo = Object.entries(vatBreakdown.vatBreakdown).reduce(
                (vbAcc, [rate, value]) => {
                    return {
                        ...vbAcc,
                        [rate]: (vbAcc[rate] ?? 0) + value,
                    };
                },
                acc.vatAmountInfo,
            );
            acc.promoCodeDiscounts = acc.promoCodeDiscounts.concat(discount ? [discount] : []);

            return acc;
        },
        {
            totalPrice: 0,
            priceToPayLater: 0,
            totalPriceExDiscounts: 0,
            totalPriceExVat: 0,
            totalVat: 0,
            vatAmountInfo: {},
            creditsUsed: 0,
            promoCodeDiscounts: [],
        } as PriceSummary,
    );
}

export function getPriceFromTicketOption(
    ticketOption: TicketOptionWithQuantity,
    ticketType?: TicketType,
    promoCodeStatus?: BilberryPromoCodeStatus | null,
    promoCodeUsageContext?: {
        promoCodeUsagesPerOrder: Map<string, number>;
        promoCodeUsages: Map<string, number>;
    },
) {
    return getPriceSummaryFromTicketOptions(
        [ticketOption],
        ticketType,
        promoCodeStatus,
        promoCodeUsageContext,
    );
}

export function getPriceFromTicketOptionNonMembership(
    ticketOption: TicketOptionWithQuantity,
    ticketType?: TicketType,
    promoCodeStatus?: BilberryPromoCodeStatus | null,
    promoCodeUsageContext?: {
        promoCodeUsagesPerOrder: Map<string, number>;
        promoCodeUsages: Map<string, number>;
    },
) {
    const productType = ticketOption.productInstances[0]?.product?.type ?? '';
    const isAccommodation = productType === 'accommodation';

    const price = ticketOption.price * ticketOption.quantity;
    const depositAmount = ticketOption.depositAmount
        ? ticketOption.depositAmount * ticketOption.quantity
        : 0;

    if (isAccommodation && ticketType) {
        const priceToPaylaterForTickeType =
            ticketType.price && ticketType.depositAmount
                ? ticketType.price - ticketType.depositAmount
                : 0;

        return {
            price: ticketType.price,
            discount: null,
            priceToPayLater: priceToPaylaterForTickeType,
        };
    }

    let discount: {
        amount: number;
        percentage: number;
        numDiscountedQuantities: number;
        code: string;
    } | null = null;

    if (promoCodeStatus && !ticketOption.membership) {
        discount = getPromoCodeDiscount(
            promoCodeStatus,
            promoCodeUsageContext,
            ticketOption.productInstances[0]?.product?.id.toString() ?? '',
            ticketOption.quantity,
            ticketOption.price,
        );
    }

    const priceToPayLater = price && depositAmount ? price - depositAmount : 0;

    return { price, discount, priceToPayLater };
}

export function getPromoCodeDiscount(
    promoCodeStatus: BilberryPromoCodeStatus,
    promoCodeUsageContext:
        | {
              promoCodeUsagesPerOrder: Map<string, number>;
              promoCodeUsages: Map<string, number>;
          }
        | undefined,
    productId: string,
    promoCodeUsages: number,
    priceOneUsage: number,
) {
    let discount: {
        amount: number;
        percentage: number;
        numDiscountedQuantities: number;
        code: string;
    } | null = null;

    const isPercent = promoCodeStatus.coupon_type === 'percent';
    const productMaxQuantityEntry = promoCodeStatus.product_max_quantity.find(
        (x) => x.product_id.toString() === productId,
    );
    if (productMaxQuantityEntry || promoCodeStatus.product_max_quantity.length === 0) {
        const usages = promoCodeUsageContext?.promoCodeUsages.get(productId ?? '');

        const entryRemainingQuantity =
            productMaxQuantityEntry?.remaining_quantity ?? Number.MAX_SAFE_INTEGER;

        const entryMaxQuantity =
            productMaxQuantityEntry?.max_quantity && productMaxQuantityEntry.max_quantity > 0
                ? productMaxQuantityEntry.max_quantity
                : Number.MAX_SAFE_INTEGER;

        const remainingQuantity =
            Math.min(entryRemainingQuantity, entryMaxQuantity) - (usages ?? 0);

        if (remainingQuantity > 0) {
            const quantitiesToDiscount = Math.min(promoCodeUsages, remainingQuantity);
            discount = {
                amount: isPercent
                    ? (promoCodeStatus.coupon_value / 100) * (priceOneUsage * quantitiesToDiscount)
                    : promoCodeStatus.coupon_value,
                percentage: isPercent ? promoCodeStatus.coupon_value : 0,
                code: promoCodeStatus.coupon_code,
                numDiscountedQuantities: quantitiesToDiscount,
            };
            promoCodeUsageContext?.promoCodeUsages.set(
                productId ?? '',
                (usages ?? 0) + promoCodeUsages,
            );
        }
    }
    return discount;
}

export function getVatBreakdownFromTicketOption(
    ticketOption: TicketOptionWithQuantity,
    ticketType?: TicketType,
    discountAmount?: number,
) {
    const productType = ticketOption.productInstances[0]?.product?.type ?? '';
    const isAccommodation = productType === 'accommodation';

    if (isAccommodation && ticketType) {
        return {
            vatBreakdown: ticketType.vatBreakdown.reduce(
                (acc, cur) => {
                    return {
                        ...acc,
                        [cur.rate]: (acc[cur.rate] ?? 0) + cur.vatAmount,
                    };
                },
                {} as Record<string, number>,
            ),
            vatAmount: ticketType.vatAmount,
        };
    }

    const fullPrice = ticketOption.price * ticketOption.quantity;
    // Math.max(0, ...) guards against flat-amount coupons that exceed the ticket price,
    // which would make discountFactor negative and produce negative VAT.
    // e.g. 100 kr coupon on a 30 kr ticket → (30 - 100) / 30 = -2.3 → clamped to 0 (VAT = 0).
    const discountFactor =
        discountAmount !== undefined && discountAmount > 0 && fullPrice > 0
            ? Math.max(0, (fullPrice - discountAmount) / fullPrice)
            : 1;

    return {
        vatAmount: ticketOption.vatAmount * ticketOption.quantity * discountFactor,
        vatBreakdown: ticketOption.vatBreakdown.reduce(
            (vbAcc, vbCur) => {
                return {
                    ...vbAcc,
                    [vbCur.rate]:
                        (vbAcc[vbCur.rate] ?? 0) +
                        vbCur.vatAmount * ticketOption.quantity * discountFactor,
                };
            },
            {} as Record<string, number>,
        ),
    };
}

export function calculateAppliedGiftcards(giftcards: BilberryGiftcardStatus[], totalPrice: number) {
    if (!giftcards) return [];
    const appliedGiftcards: AppliedGiftCard[] = [];

    for (const giftcard of giftcards) {
        const priceReducedByAppliedGiftcards = sumBy(appliedGiftcards, (x) => x.priceReduction);
        const currentTotalPrice = totalPrice - priceReducedByAppliedGiftcards;

        const priceReduction = getGiftcardSpent(giftcard, currentTotalPrice);
        const appliedGiftcard: AppliedGiftCard = {
            giftcardStatus: giftcard,
            priceReduction,
        };

        appliedGiftcards.push(appliedGiftcard);
    }

    return appliedGiftcards;
}

export function getAppliedPromoCodeIfApplicableForValueCardPurchase(
    promoCode: BilberryPromoCodeStatus | null,
    productId: number,
): AppliedPromoCode | null {
    if (!promoCode) return null;

    const isApplicableForCurrentProductPurchase = isPromoCodeApplicableForProductPurchase(
        promoCode,
        productId,
        true,
    );
    if (!isApplicableForCurrentProductPurchase) return null;

    return {
        promoCode,
        discount: promocodeAsDiscountSource(promoCode),
    };
}

export type ValueCardUsageSummary = Record<string, ValueCardUsageSummaryItem>;
export type ValueCardUsageSummaryItem = {
    valueCard: MembershipUserValueCard;
    usages: UsageOnTicket[];
};

export type UsageOnTicket = {
    productName: string;
    startAt: string;
    endAt: string;
    ticketName: string;
    ticketCurrencyCost: null | MembershipCurrencyCost;
    creditCost: number;
    priceReductionPercentage: number;
};

export function CreateValueCardUsageSummary(
    paymentPlanResponse: MembershipIntentMultiResponse,
    valueCardsForUser: MembershipUserValueCard[],
) {
    const summary: ValueCardUsageSummary = {};

    paymentPlanResponse.activityReservations.forEach((ar) => {
        return Object.values(ar.paymentPlanByTourId).forEach((p) => {
            p.tickets.forEach((t) => {
                t.valueCardUsages.forEach((vcu) => {
                    const valueCard = valueCardsForUser.find((vc) => vc.id === vcu.valueCardId);
                    if (!valueCard) return; // Shouldn't really happen I think, but since value cards and intent response are coming from different endpoints, it might happen due to timing I guess.

                    const key = `${vcu.valueCardId}`;
                    if (!summary[key]) {
                        summary[key] = {
                            valueCard: valueCard,
                            usages: [],
                        };
                    }

                    summary[key].usages.push({
                        productName: p.productName,
                        startAt: p.startAt,
                        endAt: p.endAt,
                        ticketName: t.ticketName,
                        ticketCurrencyCost: t.currencyCost,
                        creditCost: vcu.creditCost,
                        priceReductionPercentage: vcu.priceReductionPercentage,
                    });
                });
            });
        });
    });

    paymentPlanResponse.timeslotReservations.forEach((reservation) => {
        reservation.paymentPlan.tickets.forEach((t) => {
            t.valueCardUsages.forEach((vcu) => {
                const valueCard = valueCardsForUser.find((vc) => vc.id === vcu.valueCardId);
                if (!valueCard) return; // Shouldn't really happen I think, but since value cards for user and intent response are coming from different endpoints, it might happen due to timing I guess.

                const key = `${vcu.valueCardId}`;
                if (!summary[key]) {
                    summary[key] = {
                        valueCard: valueCard,
                        usages: [],
                    };
                }

                summary[key].usages.push({
                    productName: reservation.paymentPlan.productName,
                    startAt: reservation.paymentPlan.startAt,
                    endAt: reservation.paymentPlan.endAt,
                    ticketName: t.ticketName,
                    ticketCurrencyCost: t.currencyCost,
                    creditCost: vcu.creditCost,
                    priceReductionPercentage: vcu.priceReductionPercentage,
                });
            });
        });
    });

    return summary;
}
